import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { ReactComponent as ArchiveIcon } from 'assets/archive-icon.svg';

const SIDEBAR_SHADOW = '0px 6px 24px -2px rgba(0, 0, 0, 0.08), 0px 3px 6px rgba(0, 0, 0, 0.04)';

const EditAuditSidebar = ({ review, onClose, onArchive, onSave, isSaving, isArchiving }) => {
  const [title, setTitle] = useState(review.extra_data?.review_frame_name || '');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const handleSave = () => {
    const trimmed = title.trim();

    if (trimmed) {
      onSave(trimmed);
    }
  };

  const handleArchiveConfirm = () => {
    setShowArchiveConfirm(false);
    onArchive();
  };

  return (
    /*
     * Wrapper covers the full white card (position: relative parent).
     * The blur overlay sits behind the sidebar panel.
     */
    <div className="absolute inset-0 z-40">
      {/* Blur backdrop — clicking it closes the sidebar */}
      <div
        className="absolute inset-0 backdrop-blur-[4px] bg-black/[.08]"
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <div
        className="absolute right-0 top-0 h-full flex flex-col bg-white w-[400px] border-l border-[#E1E4E8]"
        style={{ boxShadow: SIDEBAR_SHADOW }}
      >
        {/* Header */}
        <div className="flex flex-row items-center px-6 h-14 border-b border-[#E5E7EB] flex-shrink-0">
          <span className="flex-1 font-inter font-semibold text-base leading-6 text-[#121723]">
            Edit flow details
          </span>

          {/* Archive icon button */}
          <button
            onClick={() => setShowArchiveConfirm(true)}
            disabled={isArchiving}
            aria-label="Archive flow"
            className="flex items-center justify-center transition-colors disabled:opacity-40 w-5 h-5"
          >
            <ArchiveIcon />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 overflow-auto p-6 gap-4">
          <div className="flex flex-col gap-12">
            {/* Label */}
            <span className="font-inter font-medium text-base leading-6 text-[#121723]">
              Title
            </span>

            {/* Input */}
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Flow title"
              className="w-full font-inter h-10 px-16 py-3 border border-[#28BDAE] rounded-lg text-sm
              leading-5 text-[#121723] outline-none box-border"
            />
          </div>
        </div>

        {/* Footer separator */}
        <div className="h-px bg-[#E5E7EB] flex-shrink-0" />

        {/* Footer */}
        <div className="flex flex-row items-center justify-end px-6 h-[72px] flex-shrink-0 gap-16">
          {/* Cancel button */}
          <button
            onClick={onClose}
            className="font-inter py-[10px] px-5 border border-[#E1E4E8] rounded-lg font-medium text-sm
            leading-5 text-[#1C2531] bg-white cursor-pointer"
            style={{ boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.04), 0px 1px 1px rgba(0,0,0,0.02)' }}
          >
            Cancel
          </button>

          {/* Update button */}
          {(() => {
            const originalTitle = (review.extra_data?.review_frame_name || '').trim();
            const isUnchanged = title.trim() === originalTitle;
            const isDisabled = isSaving || !title.trim() || isUnchanged;

            return (
              <button
                onClick={handleSave}
                disabled={isDisabled}
                className={cn(
                  'font-inter py-[10px] px-5 w-[100px] h-10 rounded-lg font-medium text-sm leading-5 border-none',
                  'transition-[background] duration-150',
                  isDisabled
                    ? 'bg-[#E1E4E8] text-[#8F97A5] cursor-not-allowed'
                    : 'bg-[#1B988E] text-white cursor-pointer'
                )}
              >
                {isSaving ? 'Updating…' : 'Update'}
              </button>
            );
          })()}
        </div>
      </div>

      {/* Archive confirmation modal — above sidebar in the stacking order */}
      {showArchiveConfirm && (
        <div
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          <div
            className="bg-white flex flex-col rounded-xl w-[500px]"
            style={{ boxShadow: '0px 8px 32px rgba(0,0,0,0.12)' }}
          >
            <div className="flex items-start justify-between p-4 px-6">
              <span className="font-inter font-semibold text-base leading-6 text-[#121723]">
                Archive this flow?
              </span>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                className="flex items-center justify-center text-[#8F97A5] ml-4 flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <p className="font-inter text-sm leading-5 text-[#606776] border-b border-t border-[#E5E7EB] p-6">
              Archiving this flow will remove edit access and hide it from the list of flows
              for all workspace members.
            </p>

            <div className="flex justify-end p-4 px-6">
              <button
                onClick={handleArchiveConfirm}
                disabled={isArchiving}
                className={cn(
                  'font-inter py-8 px-16 border border-[#FCA5A5] rounded-lg font-medium text-sm text-[#EF4444]',
                  isArchiving ? 'bg-[#FEF2F2] cursor-not-allowed' : 'bg-white cursor-pointer'
                )}
              >
                {isArchiving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

EditAuditSidebar.propTypes = {
  review: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  isArchiving: PropTypes.bool,
};

EditAuditSidebar.defaultProps = {
  isSaving: false,
  isArchiving: false,
};

export default EditAuditSidebar;
